 <!-- login page -->
<script setup lang="ts">
import {
  alertController,
  IonContent,
  IonPage,
  loadingController,
  useIonRouter,
} from '@ionic/vue';
import logoUrl from '/assets/logo.png';
import { useStore } from '@/stores/main';
import { useBackendFetch } from '@/fetch';
import { useNetwork } from '@vueuse/core';
import { FmsType } from '@/types';
import { inject, ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';

const store = useStore();
const { t } = useI18n();

const formData = reactive({
  email: '',
  password: ''
});

const errors = reactive({
  email: '',
  password: ''
});

const isSubmitting = ref(false);
const showPassword = ref(false);

// Valid email pattern
const validEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const router = useIonRouter();
const fmsType: FmsType = inject('fms-type') as FmsType;

function togglePasswordVisibility() {
  showPassword.value = !showPassword.value;
}

function validateEmail() {
  errors.email = '';
  if (!formData.email) {
    errors.email = t('emailRequired');
  } else if (!validEmailPattern.test(formData.email)) {
    errors.email = t('emailInvalid');
  }
  return !errors.email;
}

function validatePassword() {
  errors.password = '';
  if (!formData.password) {
    errors.password = t('passwordRequired');
  }
  return !errors.password;
}

async function login() {
  // Reset previous error messages
  errors.email = '';
  errors.password = '';

  // Run validations
  const isEmailValid = validateEmail();
  const isPasswordValid = validatePassword();

  // Stop execution if there are errors
  if (!isEmailValid || !isPasswordValid) {
    return;
  }

  const { isOnline } = useNetwork();

  if (!isOnline.value) {
    const alert = await alertController.create({
      header: t('noInternetTitle'),
      message: t('noInternetMessage'),
      buttons: [t('LoginPage.ok')],
      cssClass: 'custom-alert'
    });
    await alert.present();
    return;
  }

  isSubmitting.value = true;

  const loading = await loadingController.create({
    message: t('LoginPage.loading'),
    cssClass: 'custom-loading'
  });

  await loading.present();

  try {
    const { error, data, statusCode } = await useBackendFetch('/login')
      .post({ email: formData.email, password: formData.password })
      .json();

    if (error.value !== null) {
      let header = t('invalidCredentialsTitle');
      let message = t('invalidCredentialsMessage');

      if (error.value === 'Failed to fetch') {
        header = t('serverErrorTitle');
        message = t('serverErrorMessage');
      }

      if (statusCode.value === 500) {
        header = t('unknownErrorTitle');
        message = t('unknownErrorMessage');
      }

      const alert = await alertController.create({
        header,
        message,
        buttons: [t('LoginPage.ok')],
        cssClass: 'custom-alert'
      });
      await alert.present();
    } else {
      store.login(data.value);
      router.replace('/home');
    }
  } catch (err) {
    const alert = await alertController.create({
      header: t('LoginPage.genericErrorTitle'),
      message: t('LoginPage.genericErrorMessage'),
      buttons: [t('LoginPage.ok')],
      cssClass: 'custom-alert'
    });
    await alert.present();
  } finally {
    loading.dismiss();
    isSubmitting.value = false;
  }
}

function forgotPassword() {
  router.push('/forgot-password');
}
</script>

<template>
  <ion-page>
    <ion-content :fullscreen="true">
      <div class="login-container">
        <div class="login-card">
          <!-- Logo Section -->
          <div class="logo-section">
            <img :src="logoUrl" alt="Bwell Logo" class="logo" />
          </div>

          <!-- Introduction Section -->
          <div v-if="fmsType === FmsType.Work" class="intro-section">
            <h1 class="welcome-title">{{ $t('LoginPage.welcomeTitle') }}</h1>
            <p class="intro-text">
              {{ $t('LoginPage.introText') }}
            </p>
          </div>

          <!-- Form Section -->
          <form @submit.prevent="login" class="login-form">
            <h2 class="form-title">{{ $t('LoginPage.formTitle') }}</h2>
            
            <div class="form-group" :class="{ 'error': errors.email }">
              <label for="email" class="form-label">{{ $t('emailLabel') }}</label>
              <div class="input-wrapper">
                <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <input 
                  id="email" 
                  type="email" 
                  v-model="formData.email"
                  @blur="validateEmail"
                  :placeholder="$t('LoginPage.emailPlaceholder')"
                  class="form-input"
                />
              </div>
              <p v-if="errors.email" class="error-message">{{ errors.email }}</p>
            </div>

            <div class="form-group" :class="{ 'error': errors.password }">
              <label for="password" class="form-label">{{ $t('passwordLabel') }}</label>
              <div class="input-wrapper">
                <svg class="input-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"></rect>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"></path>
                </svg>
                <input 
                  id="password" 
                  :type="showPassword ? 'text' : 'password'" 
                  v-model="formData.password"
                  @blur="validatePassword"
                  :placeholder="$t('LoginPage.passwordPlaceholder')"
                  class="form-input"
                />
                <button 
                  type="button" 
                  @click="togglePasswordVisibility" 
                  class="toggle-password"
                  :aria-label="$t('LoginPage.togglePasswordAriaLabel')"
                >
                  <svg v-if="!showPassword" class="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                  <svg v-else class="eye-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                </button>
              </div>
              <p v-if="errors.password" class="error-message">{{ errors.password }}</p>
            </div>

            <div class="form-actions">
              <button 
                type="submit" 
                class="login-button" 
                :disabled="isSubmitting"
              >
                <span v-if="!isSubmitting">{{ $t('login') }}</span>
                <div v-else class="spinner"></div>
              </button>
              
              <button 
                type="button" 
                @click="forgotPassword" 
                class="forgot-password-link"
              >
                {{ $t('forgotPassword') }}
              </button>
            </div>
          </form>

          <!-- Footer Section -->
          <div class="footer-section">
            <a
              href="https://bwellacademy.com/integritetspolicy/"
              target="_blank"
              class="footer-link"
            >
              {{ $t('privacyPolicy') }}
            </a>

            <div class="divider"></div>

            <p class="copyright">{{ $t('LoginPage.copyright') }}</p>
          </div>
        </div>

        <!-- Decorative Elements -->
        <div class="decoration decoration-1"></div>
        <div class="decoration decoration-2"></div>
        <div class="decoration decoration-3"></div>
      </div>
    </ion-content>
  </ion-page>
</template>

<style scoped>
ion-content {
  --background: linear-gradient(135deg, #fffafd 0%, #fff5fa 100%);
  --padding-top: 0;
  --padding-bottom: 0;
  font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
}

.login-container {
  min-height: 100%;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  position: relative;
  overflow: hidden;
}

.login-card {
  background: white;
  width: 100%;
  max-width: 450px;
  border-radius: 16px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.08);
  padding: 2rem;
  position: relative;
  z-index: 10;
  overflow: hidden;
}

@media (min-width: 768px) {
  .login-card {
    padding: 2.5rem;
    max-width: 500px;
  }
}

@media (min-width: 1024px) {
  .login-card {
    padding: 3rem;
    max-width: 550px;
  }
}

/* Logo Section */
.logo-section {
  display: flex;
  justify-content: center;
  margin-bottom: 1.5rem;
}

.logo {
  width: 80px;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 4px 6px rgba(0, 0, 0, 0.05));
}

@media (min-width: 768px) {
  .logo {
    width: 100px;
  }
}

/* Introduction Section */
.intro-section {
  text-align: center;
  margin-bottom: 2rem;
}

.welcome-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #171717;
  margin-bottom: 0.75rem;
}

.intro-text {
  font-size: 0.95rem;
  line-height: 1.5;
  color: #4b5563;
}

@media (min-width: 768px) {
  .welcome-title {
    font-size: 1.75rem;
  }
  
  .intro-text {
    font-size: 1rem;
  }
}

/* Form Section */
.login-form {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #171717;
  margin-bottom: 0.5rem;
  text-align: center;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group.error .form-input,
.form-group.error .input-wrapper {
  border-color: #ef4444;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  transition: all 0.2s ease;
  overflow: hidden;
}

.input-wrapper:focus-within {
  border-color: #7b1fa2;
  box-shadow: 0 0 0 2px rgba(123, 31, 162, 0.1);
}

.input-icon {
  width: 18px;
  height: 18px;
  margin-left: 12px;
  color: #9ca3af;
}

.form-input {
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  outline: none;
  font-size: 1rem;
  color: #1f2937;
  background: transparent;
  width: 100%;
}

.form-input::placeholder {
  color: #9ca3af;
}

.toggle-password {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0.5rem;
  margin-right: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.eye-icon {
  width: 18px;
  height: 18px;
  color: #9ca3af;
}

.error-message {
  font-size: 0.75rem;
  color: #ef4444;
  margin-top: 0.25rem;
}

.form-actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 0.5rem;
}

.login-button {
  background-color: #7b1fa2;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 48px;
}

.login-button:hover:not(:disabled) {
  background-color: #6a1b9a;
  transform: translateY(-1px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.login-button:active:not(:disabled) {
  transform: translateY(0);
}

.login-button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.forgot-password-link {
  background: none;
  border: none;
  color: #7b1fa2;
  font-size: 0.875rem;
  padding: 0;
  text-align: center;
  text-decoration: underline;
  cursor: pointer;
  transition: color 0.2s ease;
}

.forgot-password-link:hover {
  color: #6a1b9a;
}

/* Footer Section */
.footer-section {
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.footer-link {
  color: #7b1fa2;
  font-size: 0.75rem;
  text-decoration: none;
  transition: color 0.2s ease;
}

.footer-link:hover {
  color: #6a1b9a;
  text-decoration: underline;
}

.divider {
  width: 50px;
  height: 1px;
  background-color: #e5e7eb;
}

.copyright {
  font-size: 0.75rem;
  color: #9ca3af;
  text-align: center;
}

/* Decorative Elements */
.decoration {
  position: absolute;
  border-radius: 50%;
  z-index: 1;
  opacity: 0.5;
}

.decoration-1 {
  width: 300px;
  height: 300px;
  background: linear-gradient(135deg, rgba(123, 31, 162, 0.1), rgba(123, 31, 162, 0.05));
  top: -100px;
  right: -100px;
}

.decoration-2 {
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, rgba(123, 31, 162, 0.08), rgba(123, 31, 162, 0.03));
  bottom: -50px;
  left: -50px;
}

.decoration-3 {
  width: 150px;
  height: 150px;
  background: linear-gradient(135deg, rgba(123, 31, 162, 0.06), rgba(123, 31, 162, 0.02));
  top: 40%;
  left: 25%;
  transform: translateX(-50%);
}

/* Responsive Adjustments for iPad and larger screens */
@media (min-width: 768px) and (min-height: 1024px) {
  .login-card {
    transform: scale(1.05);
  }
}

@media (min-width: 1024px) {
  .login-container {
    padding: 2rem;
  }
  
  .decoration-1 {
    width: 400px;
    height: 400px;
  }
  
  .decoration-2 {
    width: 300px;
    height: 300px;
  }
  
  .decoration-3 {
    width: 200px;
    height: 200px;
  }
}

/* Custom Alert and Loading Styles */
:global(.custom-alert) {
  --background: white;
  --border-radius: 12px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

:global(.custom-loading) {
  --background: rgba(255, 255, 255, 0.9);
  --spinner-color: #7b1fa2;
  --border-radius: 12px;
  --box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}
</style>
